/** Franchise — création de son propre réseau de franchisés (30/06).
 *  Déblocage : >= 1 an d'exploitation + >= 3 magasins en propre dans >= 2 villes.
 *  Coût de structuration : 25 000 € (manuel opératoire, concept, juridique).
 *  Conditions (droit d'entrée + redevance % du résultat) => attractivité :
 *  plus elles sont douces, plus les candidatures sont fréquentes. */
import { VILLES, magasinsParVille } from './villes';

export const COUT_STRUCTURATION_FRANCHISE = 25000;

export const DROITS_ENTREE = [
  { montant: 5000, label: 'Accessible' },
  { montant: 15000, label: 'Standard' },
  { montant: 30000, label: 'Exigeant' },
];

export const REDEVANCES_PCT = [
  { pct: 2, label: 'Douce' },
  { pct: 5, label: 'Standard' },
  { pct: 8, label: 'Élevée' },
];

export const JOURS_MIN_EXPLOITATION = 365;
export const MAGASINS_MIN = 3;
export const VILLES_MIN = 2;

/** Conditions de déblocage : 1 an + 3 magasins en propre dans 2 villes. */
export const franchiseDebloquee = (retail, totalDays) => {
  const parVille = magasinsParVille(retail);
  const nbMagasins = Object.values(parVille).reduce((s, l) => s + l.length, 0);
  const nbVilles = Object.keys(parVille).length;
  return {
    ok: totalDays >= JOURS_MIN_EXPLOITATION && nbMagasins >= MAGASINS_MIN && nbVilles >= VILLES_MIN,
    anciennete: totalDays >= JOURS_MIN_EXPLOITATION,
    magasins: nbMagasins,
    villes: nbVilles,
  };
};

/** Score d'attractivité 0 (dur) → 4 (doux) selon les conditions choisies. */
const scoreDurete = (droitEntree, redevancePct) => {
  const iD = Math.max(0, DROITS_ENTREE.findIndex(d => d.montant === droitEntree));
  const iR = Math.max(0, REDEVANCES_PCT.findIndex(r => r.pct === redevancePct));
  return iD + iR; // 0 (5k/2%) → 4 (30k/8%)
};

/** Délai (jours) avant la prochaine candidature : ~2 sem. (doux) → ~7 sem. (dur). */
export const delaiProchaineCandidature = (droitEntree, redevancePct) => {
  const base = 13 + scoreDurete(droitEntree, redevancePct) * 9;
  return base + Math.floor(Math.random() * (base / 2));
};

/** Libellé d'attractivité affiché au joueur. */
export const attractiviteLabel = (droitEntree, redevancePct) => {
  const sc = scoreDurete(droitEntree, redevancePct);
  if (sc <= 1) return { label: 'Très attractif', detail: '≈ 1 candidature / 2-3 semaines', tone: 'money' };
  if (sc <= 2) return { label: 'Attractif', detail: '≈ 1 candidature / 3-5 semaines', tone: 'accent' };
  if (sc <= 3) return { label: 'Sélectif', detail: '≈ 1 candidature / 5-6 semaines', tone: 'warn' };
  return { label: 'Très sélectif', detail: '≈ 1 candidature / 6-8 semaines', tone: 'danger' };
};

const PRENOMS = ['Camille', 'Julien', 'Sofia', 'Marc', 'Léa', 'Nadia', 'Hugo', 'Claire', 'Karim', 'Élodie', 'Paul', 'Inès'];
const NOMS = ['Bertin', 'Moreau', 'Delacroix', 'N\'Diaye', 'Fontaine', 'Garcia', 'Lemoine', 'Roussel', 'Benali', 'Marchand'];

/** Villes éligibles : sans magasin en propre et sans franchisé (France + Europe débloquée exclue pour rester simple). */
export const villesLibresFranchise = (retail) => {
  const parVille = magasinsParVille(retail);
  const occupees = new Set(Object.keys(parVille));
  (retail?.franchise?.franchises || []).forEach(f => occupees.add(f.villeId));
  return VILLES.filter(v => !v.international && !occupees.has(v.id));
};

/** Génère un candidat franchisé sur une ville libre (pondéré par le bassin). */
export const genererCandidatFranchise = (retail, jour) => {
  const libres = villesLibresFranchise(retail);
  if (!libres.length) return null;
  const totalBassin = libres.reduce((s, v) => s + (v.bassin || 1), 0);
  let tirage = Math.random() * totalBassin;
  let ville = libres[0];
  for (const v of libres) { tirage -= (v.bassin || 1); if (tirage <= 0) { ville = v; break; } }
  const nom = `${PRENOMS[Math.floor(Math.random() * PRENOMS.length)]} ${NOMS[Math.floor(Math.random() * NOMS.length)]}`;
  const resultatMensuel = Math.round((2000 + Math.random() * 2500) * (ville.bassin || 1) / 50) * 50;
  return {
    id: `fr-${jour}-${Math.floor(Math.random() * 1e6).toString(36)}`,
    nom,
    villeId: ville.id,
    villeNom: ville.nom,
    resultatMensuel,
    jour,
    expireJour: jour + 6,
  };
};

/** Résiliation d'un franchisé à chaque échéance mensuelle — volontairement rare. */
export const PROBA_RESILIATION_MENSUELLE = 0.025;

/** Événement « aide franchisé » : choix d'accompagnement et impact sur son résultat. */
export const AIDE_FRANCHISE_CHOIX = [
  { id: 'financer', label: "Financer l'animation complète", cout: 800, effetPct: 15, detail: "Vous payez l'animation commerciale (800 €). Résultat mensuel du franchisé : +15 % durable — et vos redevances suivent." },
  { id: 'cofinancer', label: 'Co-financer avec le franchisé', cout: 300, effetPct: 7, detail: 'Vous partagez les frais (300 €). Résultat mensuel : +7 %. Un bon compromis réseau.' },
  { id: 'refuser', label: 'Refuser poliment', cout: 0, effetPct: -8, detail: "Aucun coût aujourd'hui, mais le point de vente s'essouffle : résultat mensuel −8 % et un franchisé déçu." },
];

/** Probabilité quotidienne qu'un franchisé demande de l'aide (réseau actif). */
export const probaAideFranchiseJour = (nbFranchises) => Math.min(0.05, 0.012 * nbFranchises);

/** Synthèse franchise pour le débrief PDF / la télémétrie formateur. */
export const bilanFranchise = (retail) => {
  const fr = retail?.franchise;
  if (!fr?.cree) return null;
  return {
    droitEntree: fr.droitEntree,
    redevancePct: fr.redevancePct,
    nbFranchises: (fr.franchises || []).length,
    perdus: fr.perdus || 0,
    villes: (fr.franchises || []).map((f) => f.villeNom),
    totalDroitsEntree: fr.totalDroitsEntree || 0,
    totalRedevances: fr.totalRedevances || 0,
    aides: fr.aidesStats || { demandes: 0, financees: 0, refusees: 0 },
  };
};
